import { currentEapUtilisationQuery } from "@/api/reporting";
import { ChartBox } from "@/components/charts/ChartBox";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { Card } from "@/components/ui/Card";
import { Stat, StatLabel, StatList, StatValue } from "@/components/ui/Stat";
import { formatDate, toDateOnly } from "@common/utils/date";
import { formatWholeNumber } from "@common/utils/i18n";
import { Box, Center, Checkbox, Flex, Input, Label, Select, Span, Spinner, Text } from "@givenwell/components";
import { EapUtilisationDataPoint } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { barY, defineChart, rect } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { Chart as TooltipChart } from "@tanstack/charts/react/tooltip";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { boolean, custom, fallback, object, optional, picklist, pipe, string } from "valibot";

/**
 * How a period's utilisation rate is calculated. Both are "used ÷ available" as a percentage;
 * they differ in what is counted.
 */
const MEASURES = {
  members: {
    label: "Unique users",
    /** Column header, tooltip row, and stat wording for the numerator. */
    used: "Members with usage",
    /** Wording for the denominator, lower case for use mid-sentence. */
    available: "eligible members",
    /** How the rate is calculated, for the histogram description. */
    formula: "members with at least one session divided by eligible members",
    usedOf: (p: EapUtilisationDataPoint) => p.members_with_usage_count,
    availableOf: (p: EapUtilisationDataPoint) => p.eligible_member_count,
  },
  sessions: {
    label: "Session usage",
    used: "Sessions used",
    available: "available sessions",
    formula: "sessions used divided by the total sessions available",
    usedOf: (p: EapUtilisationDataPoint) => p.sessions_used,
    availableOf: (p: EapUtilisationDataPoint) => p.total_available_sessions,
  },
} satisfies Record<
  string,
  {
    label: string;
    used: string;
    available: string;
    formula: string;
    usedOf: (p: EapUtilisationDataPoint) => number;
    availableOf: (p: EapUtilisationDataPoint) => number;
  }
>;
type Measure = keyof typeof MEASURES;
const MEASURE_KEYS = Object.keys(MEASURES) as [Measure, ...Measure[]];

const today = new Date();
const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());

const isDateString = () =>
  custom<string>(input => (typeof input === "string" ? /^\d{4}-\d{2}-\d{2}$/.test(input) : false));

export const Route = createFileRoute("/dashboard/eap-utilisation")({
  component: Page,
  validateSearch: object({
    start: fallback(pipe(optional(string(), toDateOnly(oneYearAgo)), isDateString()), toDateOnly(oneYearAgo)),
    end: fallback(pipe(optional(string(), toDateOnly(today)), isDateString()), toDateOnly(today)),
    includeGivenwell: fallback(optional(boolean(), false), false),
    measure: fallback(optional(picklist(MEASURE_KEYS), "members"), "members"),
  }),
  beforeLoad: () => ({
    breadcrumb: "EAP Utilisation",
  }),
});

function Page() {
  const nav = Route.useNavigate();
  const { start, end, includeGivenwell, measure } = Route.useSearch();

  const { data, isPending } = useQuery(
    currentEapUtilisationQuery({ startDate: start, endDate: end, includeGivenwellSupporters: includeGivenwell }),
  );

  const buckets = useMemo(() => bucketByMaturity(data ?? [], measure), [data, measure]);
  const totals = useMemo(() => summarise(data ?? [], measure), [data, measure]);
  const leaders = useMemo(() => rankPeriods(data ?? [], measure), [data, measure]);
  const wording = MEASURES[measure];

  return (
    <Box css={{ height: "100%", overflow: "auto" }}>
      <Flex css={{ gap: 24, marginBottom: 8, height: 52, items: "center" }}>
        <Flex css={{ gap: 4, items: "center" }}>
          <Input
            aria-label="Period start from"
            type="date"
            size="sm"
            css={{ width: 160 }}
            value={start}
            max={end}
            onValueChange={value => value && nav({ search: prev => ({ ...prev, start: value }) })}
          />
          –
          <Input
            aria-label="Period start to"
            type="date"
            size="sm"
            css={{ width: 160 }}
            value={end}
            min={start}
            onValueChange={value => value && nav({ search: prev => ({ ...prev, end: value }) })}
          />
        </Flex>
        <Label css={{ d: "flex", gap: 8, items: "center" }}>
          <Checkbox
            checked={includeGivenwell}
            onCheckedChange={checked => nav({ search: prev => ({ ...prev, includeGivenwell: checked === true }) })}
          />
          <Text>Include Givenwell supporters</Text>
        </Label>
        <Flex css={{ alignItems: "center", gap: 4 }}>
          <Span css={{ fontScale: "sm" }}>Measure utilisation by: </Span>
          <Select
            aria-label="Utilisation measure"
            css={{ width: 180 }}
            size="sm"
            value={measure}
            onValueChange={value => nav({ search: prev => ({ ...prev, measure: value as Measure }) })}
          >
            {MEASURE_KEYS.map(key => (
              <option key={key} value={key}>
                {MEASURES[key].label}
              </option>
            ))}
          </Select>
        </Flex>
      </Flex>

      <Card title="Average utilisation">
        {isPending ? (
          <Center css={{ height: 72 }}>
            <Spinner />
          </Center>
        ) : (
          <StatList>
            <Stat css={{ flex: "1 0 0px" }}>
              <StatLabel>Average utilisation rate</StatLabel>
              <StatValue>{totals.ratedPeriods > 0 ? formatRate(totals.meanRate) : "—"}</StatValue>
              <Text css={{ fontScale: "sm", color: colors.text2 }}>
                Mean of each period's own rate. Every period counts equally, whatever its size or maturity.
              </Text>
            </Stat>
            <Stat css={{ flex: "1 0 0px" }}>
              <StatLabel>Pooled utilisation rate</StatLabel>
              <StatValue>
                {totals.ratedPeriods > 0 ? formatRate(totals.rate) : "—"}
                {totals.ratedPeriods > 0 && (
                  <Span css={{ fontSize: "0.5em", color: colors.text2, fontWeight: 500, ml: 8 }}>
                    ({formatWholeNumber(totals.used)} of {formatWholeNumber(totals.available)})
                  </Span>
                )}
              </StatValue>
              <Text css={{ fontScale: "sm", color: colors.text2 }}>
                All {wording.used.toLowerCase()} divided by all {wording.available}. Larger periods carry more weight.
              </Text>
            </Stat>
            <Stat css={{ flex: "1 0 0px" }}>
              <StatLabel>Periods</StatLabel>
              <StatValue>{formatWholeNumber(totals.periods)}</StatValue>
            </Stat>
            <Stat css={{ flex: "1 0 0px" }}>
              <StatLabel>Supporters</StatLabel>
              <StatValue>{formatWholeNumber(totals.supporters)}</StatValue>
            </Stat>
          </StatList>
        )}
      </Card>
      <Box css={{ height: 24 }} />
      <Card
        title="Utilisation Leaderboard"
        description="The ten periods with the highest utilisation rate in the selected range. Click a row to open that supporter's EAP report in a new tab."
      >
        <Leaderboard rows={leaders} measure={measure} isPending={isPending} />
      </Card>
      <Box css={{ height: 24 }} />
      <Card
        title="Current EAP utilisation by period maturity"
        description={`Every EAP period that started between the selected dates, grouped by how far through it is today (whole months since the period start, capped at the period end). Bars show the pooled utilisation rate for each group: ${wording.formula}.`}
      >
        {isPending ? (
          <Center css={{ height: CHART_HEIGHT }}>
            <Spinner />
          </Center>
        ) : buckets.length === 0 ? (
          <Center css={{ height: CHART_HEIGHT }}>
            <Text css={{ color: colors.text2 }}>No EAP periods started in this date range.</Text>
          </Center>
        ) : (
          <MaturityHistogram buckets={buckets} measure={measure} />
        )}
      </Card>
    </Box>
  );
}

const CHART_HEIGHT = 400;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type MaturityBucket = {
  /** Whole months since the period start. Bucket `n` covers maturities in [n, n + 1) months. */
  months: number;
  periods: number;
  supporters: number;
  supporterIds: string[];
  /** Denominator summed over the bucket: eligible members or available sessions, per the measure. */
  available: number;
  /** Numerator summed over the bucket: members with usage or sessions used, per the measure. */
  used: number;
  /** Pooled utilisation rate as a percentage. */
  rate: number;
  /** Every period in the bucket with its own rate, highest first. */
  periodRates: PeriodRate[];
};

type PeriodRate = {
  /** Zero-based position once sorted by rate, used as the mini chart's band. */
  rank: string;
  periodId: string;
  supporterId: string;
  rate: number;
};

/** Parses a `YYYY-MM-DD` date-only string as UTC midnight. */
function parseDateOnly(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

/** Whole calendar months from `from` to `to` (UTC), ignoring any partial month. */
function wholeMonthsBetween(from: Date, to: Date): number {
  let months = (to.getUTCFullYear() - from.getUTCFullYear()) * 12 + (to.getUTCMonth() - from.getUTCMonth());
  if (to.getUTCDate() < from.getUTCDate()) months -= 1;
  return Math.max(0, months);
}

/**
 * Maturity is the time elapsed since the period started, capped at the period end. The
 * end date is inclusive, so a finished period is measured to the day after it ended: a
 * 1 Jan – 31 Dec period counts as a full 12 months rather than 11 months and 30 days.
 */
function periodMaturityMonths(point: EapUtilisationDataPoint, now: Date): number | undefined {
  const start = parseDateOnly(point.period_start_date);
  const endExclusive = new Date(parseDateOnly(point.period_end_date).getTime() + MS_PER_DAY);
  const cappedNow = now < endExclusive ? now : endExclusive;
  if (cappedNow < start) return undefined; // Period hasn't started yet.
  return wholeMonthsBetween(start, cappedNow);
}

function bucketByMaturity(points: EapUtilisationDataPoint[], measure: Measure): MaturityBucket[] {
  const { usedOf, availableOf } = MEASURES[measure];
  const now = parseDateOnly(toDateOnly(new Date()));
  const groups = new Map<
    number,
    { periods: number; supporters: Set<string>; available: number; used: number; points: EapUtilisationDataPoint[] }
  >();

  for (const point of points) {
    const months = periodMaturityMonths(point, now);
    if (months === undefined) continue;
    const group = groups.get(months) ?? {
      periods: 0,
      supporters: new Set<string>(),
      available: 0,
      used: 0,
      points: [],
    };
    group.periods += 1;
    group.points.push(point);
    group.supporters.add(point.supporter_id);
    group.available += availableOf(point);
    group.used += usedOf(point);
    groups.set(months, group);
  }

  return [...groups.entries()]
    .map(([months, group]) => ({
      months,
      periods: group.periods,
      supporters: group.supporters.size,
      supporterIds: [...group.supporters].sort(),
      available: group.available,
      used: group.used,
      rate: group.available > 0 ? (group.used / group.available) * 100 : 0,
      periodRates: [...group.points]
        .sort((a, b) => periodRate(b, measure) - periodRate(a, measure))
        .map((p, rank) => ({
          rank: String(rank),
          periodId: p.period_id,
          supporterId: p.supporter_id,
          rate: periodRate(p, measure),
        })),
    }))
    .sort((a, b) => a.months - b.months);
}

function summarise(points: EapUtilisationDataPoint[], measure: Measure) {
  const { usedOf, availableOf } = MEASURES[measure];
  const supporters = new Set(points.map(p => p.supporter_id));
  const available = points.reduce((sum, p) => sum + availableOf(p), 0);
  const used = points.reduce((sum, p) => sum + usedOf(p), 0);
  // Only periods with something available have a rate, so the mean is taken over those.
  const rated = points.filter(p => availableOf(p) > 0);
  const meanRate = rated.length > 0 ? rated.reduce((sum, p) => sum + periodRate(p, measure), 0) / rated.length : 0;
  return {
    periods: points.length,
    ratedPeriods: rated.length,
    supporters: supporters.size,
    available,
    used,
    /** Pooled rate: everything used over everything available. */
    rate: available > 0 ? (used / available) * 100 : 0,
    /** Unweighted mean of each period's own rate. */
    meanRate,
  };
}

function formatRate(rate: number) {
  return `${rate.toFixed(1)}%`;
}

function describeBucket(bucket: MaturityBucket) {
  return `${bucket.months}–${bucket.months + 1} months`;
}

const TOOLTIP_WIDTH = 360;
const TOOLTIP_GAP = 8;

/**
 * Bar chart of each period's own rate inside the tooltip, highest first. A tooltip body is
 * inert until pinned, so the bars only become interactive (hover highlight, click to open the
 * supporter's EAP report) once the reader has clicked the histogram bar to pin it.
 */
function BucketPeriodsChart({ bucket, pinned }: { bucket: MaturityBucket; pinned: boolean }) {
  const router = useRouter();

  const definition = useMemo(
    () =>
      defineChart({
        marks: [
          barY(bucket.periodRates, {
            x: "rank",
            y: "rate",
            fill: colors.cyan,
            inset: bucket.periodRates.length > 40 ? 0 : 1,
            states: [{ when: { focus: "primary" }, style: { fill: colors.blue500 } }],
          }),
        ],
        scales: {
          x: {
            scale: scaleBand<string>()
              .domain(bucket.periodRates.map(p => p.rank))
              .padding(0.1),
            grid: false,
            axis: false,
          },
          y: {
            // Session usage can exceed the entitlement, so let the ceiling grow past 100% when it does.
            scale: scaleLinear().domain([0, Math.max(100, ...bucket.periodRates.map(p => p.rate))]),
            grid: true,
            axis: {
              line: false,
              ticks: { values: [0, 50, 100], size: 0, format: v => `${v}%` },
              tickLabels: { fontSize: 11, opacity: 1 },
            },
          },
        },
        margin: { top: 4, right: 0, bottom: 4 },
        theme: { grid: colors.outline2 },
        // Only the pinned body can take input; while transient the chart is display-only.
        focus: pinned ? "nearest-x" : false,
        focusRing: false,
        pointer: pinned,
        keyboard: pinned,
        // Bars can be a few pixels wide in a dense bucket, so snap generously.
        maxFocusDistance: Number.POSITIVE_INFINITY,
      }),
    [bucket, pinned],
  );

  return (
    <Box
      css={{
        mt: 8,
        pt: 8,
        borderTop: `1px solid ${colors.outline2}`,
        "& .ts-chart__grid": { strokeOpacity: 1 },
        "& .ts-chart": { cursor: pinned ? "pointer" : undefined },
      }}
    >
      <Text css={{ fontSize: 13, weight: 600, mb: 4 }}>Distribution of utilisation rates</Text>
      <Chart
        definition={definition}
        height={110}
        ariaLabel={`Utilisation rate of each of the ${bucket.periodRates.length} periods in this bucket`}
        onSelect={point => {
          if (!pinned || !point) return;
          // Open in a new tab so the pinned tooltip and its filters stay put on this page.
          const { href } = router.buildLocation({
            to: "/supporter/$supporterId/eap",
            params: { supporterId: point.datum.supporterId },
            search: { periodId: point.datum.periodId },
          });
          window.open(href, "_blank", "noopener");
        }}
      />
      <Text css={{ fontSize: 12, color: colors.text2, mt: 4 }}>
        {pinned
          ? "Click a period to open its supporter's EAP report in a new tab."
          : "Click the histogram bar to pin this, then click a period to open its supporter's EAP report in a new tab."}
      </Text>
    </Box>
  );
}

function periodRate(point: EapUtilisationDataPoint, measure: Measure): number {
  const { usedOf, availableOf } = MEASURES[measure];
  const available = availableOf(point);
  return available > 0 ? (usedOf(point) / available) * 100 : 0;
}

/** The ten periods with the highest utilisation rate. Periods with nothing available can't be rated. */
function rankPeriods(points: EapUtilisationDataPoint[], measure: Measure): EapUtilisationDataPoint[] {
  const { usedOf, availableOf } = MEASURES[measure];
  return points
    .filter(p => availableOf(p) > 0)
    .sort((a, b) => periodRate(b, measure) - periodRate(a, measure) || usedOf(b) - usedOf(a))
    .slice(0, 10);
}

function Leaderboard({
  rows,
  measure,
  isPending,
}: {
  rows: EapUtilisationDataPoint[];
  measure: Measure;
  isPending: boolean;
}) {
  const router = useRouter();
  const { usedOf, availableOf } = MEASURES[measure];

  return (
    <DataTableBody
      data={rows}
      dataKey="period_id"
      status={isPending ? "pending" : "success"}
      emptyStateMessage="No rated EAP periods in this date range."
      rounded="full"
      compact
      selectionType="click"
      onSelectionChange={period => {
        const { href } = router.buildLocation({
          to: "/supporter/$supporterId/eap",
          params: { supporterId: period.supporter_id },
          search: { periodId: period.period_id },
        });
        window.open(href, "_blank", "noopener");
      }}
      columns={[
        {
          header: "Supporter ID",
          cell: "supporter_id",
          css: { fontFamily: "monospace" },
        },
        {
          header: "Period",
          columnKey: "period",
          cell: period => `${formatDate(period.period_start_date, true)} – ${formatDate(period.period_end_date, true)}`,
          getValue: period => period.period_start_date,
        },
        {
          header: "Utilisation rate",
          columnKey: "rate",
          textAlign: "right",
          cell: period => (
            <>
              {formatRate(periodRate(period, measure))}{" "}
              <Text as="span" css={{ color: colors.text2, fontScale: "sm" }}>
                ({formatWholeNumber(usedOf(period))} of {formatWholeNumber(availableOf(period))})
              </Text>
            </>
          ),
          getValue: period => periodRate(period, measure),
          sortingFn: "number",
        },
      ]}
    />
  );
}

function MaturityHistogram({ buckets, measure }: { buckets: MaturityBucket[]; measure: Measure }) {
  const definition = useMemo(() => {
    const wording = MEASURES[measure];
    const maxMonths = Math.max(...buckets.map(b => b.months)) + 1;
    const maxRate = Math.max(...buckets.map(b => b.rate));
    // Round the y domain up to a sensible ceiling so the tallest bar doesn't touch the top.
    // Session usage can exceed the entitlement, so only clamp to 100% while nothing is above it.
    const padded = Math.max(10, Math.ceil((maxRate * 1.1) / 10) * 10);
    const rateLimit = maxRate > 100 ? padded : Math.min(100, padded);
    const monthTicks = Array.from({ length: maxMonths + 1 }, (_, i) => i);

    return defineChart({
      marks: [
        rect(buckets, {
          id: "utilisation",
          x1: b => b.months,
          x2: b => b.months + 1,
          y1: () => 0,
          y2: "rate",
          fill: colors.cyan,
          inset: 1,
          states: [
            {
              when: { focus: "primary" },
              style: { fill: colors.blue500 },
              transition: { type: "tween", duration: 140, easing: "ease-out" },
            },
          ],
        }),
      ],
      scales: {
        x: {
          scale: scaleLinear().domain([0, maxMonths]),
          axis: {
            line: false,
            label: { text: "EAP period maturity (months since EAP period started)" },
            ticks: { values: monthTicks, size: 0, format: value => String(value) },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
        y: {
          scale: scaleLinear().domain([0, rateLimit]),
          grid: true,
          axis: {
            line: false,
            label: { text: "Utilisation rate" },
            ticks: { spacing: 50, size: 0, format: value => `${value}%` },
            tickLabels: { fontSize: 12, opacity: 1 },
          },
        },
      },
      theme: { grid: colors.outline2 },
      tooltip: {
        use: tooltip,
        // "right" puts the tooltip's left edge at anchor.x + offset, so the anchor decides
        // which side of the bar it lands on: the right edge when it fits, else backed off
        // by the tooltip width so it sits fully left of the bar.
        placement: "right",
        offset: TOOLTIP_GAP,
        anchor: (points, { scales, surface }) => {
          const point = points[0];
          const mapX = scales.x?.viewport?.map ?? scales.x?.map;
          const mapY = scales.y?.viewport?.map ?? scales.y?.map;
          if (!point || !mapX || !mapY) return null;
          const x1 = mapX(point.x1Value);
          const x2 = mapX(point.x2Value);
          if (!Number.isFinite(x1) || !Number.isFinite(x2)) return null;
          const barLeft = Math.min(x1, x2);
          const barRight = Math.max(x1, x2);
          const fitsRight = barRight + TOOLTIP_GAP + TOOLTIP_WIDTH + 8 <= surface.width;
          const x = fitsRight ? barRight : barLeft - TOOLTIP_WIDTH - 2 * TOOLTIP_GAP;
          // Anchor at the bar's vertical midpoint so the tooltip centres on the bar itself.
          const yTop = mapY(point.datum.rate);
          const yBase = mapY(0);
          const y = Number.isFinite(yTop) && Number.isFinite(yBase) ? (yTop + yBase) / 2 : point.y;
          return { x, y };
        },
        items: [
          { id: "utilisation", label: "Maturity", text: point => describeBucket(point.datum) },
          { id: "utilisation", label: "Utilisation rate", text: point => formatRate(point.datum.rate) },
          {
            id: "utilisation",
            label: wording.used,
            text: point =>
              `${formatWholeNumber(point.datum.used)} of ${formatWholeNumber(point.datum.available)} ${wording.available}`,
          },
          {
            id: "utilisation",
            label: "Periods",
            text: point =>
              `${formatWholeNumber(point.datum.periods)} across ${formatWholeNumber(point.datum.supporters)} supporters`,
          },
        ],
        className: "eap-utilisation-tooltip",
      },
      focus: "group-x",
      focusRing: false,
      maxFocusDistance: Number.POSITIVE_INFINITY,
    });
  }, [buckets, measure]);

  return (
    <ChartBox
      css={{
        "& .ts-chart__grid": { strokeOpacity: 1 },
        // Fixed width so the nested chart has a size to fill; the supporter ID list wraps within it.
        "& .eap-utilisation-tooltip": {
          boxSizing: "border-box",
          width: TOOLTIP_WIDTH,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        },
      }}
    >
      <TooltipChart
        definition={definition}
        height={CHART_HEIGHT}
        ariaLabel="Current EAP utilisation by period maturity"
        renderTooltipBody={({ points, defaultBody, pinned }) => {
          const bucket = points[0]?.datum;
          if (!bucket) return defaultBody;
          return (
            <Box>
              {defaultBody}
              <BucketPeriodsChart bucket={bucket} pinned={pinned} />
            </Box>
          );
        }}
      />
    </ChartBox>
  );
}
